import { Button } from "@/components/core/buttons/Button";
import { cn } from "@/lib/utils";
import React, { ComponentProps, forwardRef } from "react";

import { IconBox } from "../IconBox";
import { Spinner } from "../Spinner";

type IconButtonProps = {
    name: IconName;
    iconRef?: React.Ref<HTMLElement>;
    noIconSize?: boolean;
} & Omit<ComponentProps<typeof Button>, "startIcon" | "endIcon">;

// Helper to normalize size for lookups (maps icon-* sizes to base sizes)
const normalizeSize = (
    size:
        | "xl"
        | "large"
        | "medium"
        | "small"
        | "icon-xl"
        | "icon-large"
        | "icon-medium"
        | "icon-small"
        | null
        | undefined
): "xl" | "large" | "medium" | "small" => {
    if (!size) return "medium";
    if (size.startsWith("icon-")) {
        const iconSize = size.replace("icon-", "") as
            | "xl"
            | "large"
            | "medium"
            | "small";
        return iconSize;
    }
    return size as "xl" | "large" | "medium" | "small";
};

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
    (
        {
            size = "medium",
            name,
            iconRef,
            disabled = false,
            loading = false,
            className,
            noIconSize = false,
            ...props
        },
        ref: React.Ref<HTMLButtonElement>
    ) => {
        const resolvedSize = size || "medium";
        const normalizedSize = normalizeSize(resolvedSize);
        const isIconSize = resolvedSize?.startsWith("icon-") ?? false;

        // For icon sizes, Button already applies p-0, so we don't need padding classes
        const paddingClasses = isIconSize
            ? undefined
            : {
                  xl: "p-3",
                  large: "p-2",
                  medium: "p-1",
                  small: "p-0.5"
              }[normalizedSize];

        /**
         * iconBoxWrapSize
         *
         * IconButton과 Button의 높이를 일치시키기 위해 사용됩니다.
         * 텍스트만 있는 Button과 IconButton이 동일한 높이를 가지도록 하기 위해
         * typography.button의 line-height를 최소 높이로 가진 div로 IconButton을 감싸서 높이를 조절합니다.
         */
        const iconBoxWrapSize = {
            xl: 32,
            large: 28,
            medium: 24,
            small: 24
        }[normalizedSize];

        const iconBoxWrapClasses = {
            xl: "w-8 h-8",
            large: "w-7 h-7",
            medium: "w-6 h-6",
            small: "w-6 h-6"
        }[normalizedSize];

        return (
            <Button
                className={cn(paddingClasses, className)}
                ref={ref}
                size={resolvedSize}
                disabled={disabled || loading}
                {...props}
            >
                <div
                    className={cn(
                        "flex items-center justify-center",
                        iconBoxWrapClasses
                    )}
                >
                    {loading ? (
                        <Spinner
                            size={iconBoxWrapSize * 0.7}
                            color="currentColor"
                        />
                    ) : (
                        <IconBox
                            noIconSize={noIconSize}
                            ref={iconRef}
                            name={name}
                            size={normalizedSize}
                        />
                    )}
                </div>
            </Button>
        );
    }
);

IconButton.displayName = "IconButton";
